import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { RetentionSource, useRetention } from "./useRetention";

const WEEK_OPTIONS = [8, 12, 16, 26];

const SOURCE_META: Record<
  RetentionSource,
  { label: string; unit: string; title: string; description: string }
> = {
  sessions: {
    label: "Sessions (cookies)",
    unit: "Sessions",
    title: "Session retention by weekly cohort",
    description:
      "A session counts as active in week N after creation if it was last " +
      "used during or after that week. Week 0 is the creation week. Weeks " +
      "are Monday-aligned, UTC.",
  },
  "refresh-tokens": {
    label: "Refresh tokens",
    unit: "Tokens",
    title: "Refresh token retention by weekly cohort",
    description:
      "Rotating tokens are grouped into rotation families, so each unit is " +
      "a device or app that got a refresh token. A family counts as active " +
      "in week N if it was last exchanged during or after that week. Weeks " +
      "are Monday-aligned, UTC.",
  },
};

// Sequential saffron ramp (the brand colour sits mid-way), more = darker.
// Cell fills are explicit hexes so they read the same in light and dark mode;
// the ink is flipped per step for contrast.
const RAMP = [
  "#fff6dc",
  "#ffefc2",
  "#ffe7a6",
  "#ffdd85",
  "#ffd35f",
  "#ffc93c",
  "#f2b52a",
  "#e09f1f",
  "#c98818",
  "#b87a17",
  "#8e5a10",
  "#6f450c",
  "#523308",
];

// Square-root scale: weekly retention drops steeply, and a linear mapping
// would leave everything past week 1 nearly white.
function cellStyle(pct: number): { backgroundColor: string; color: string } {
  const idx = Math.min(
    RAMP.length - 1,
    Math.round(Math.sqrt(pct / 100) * (RAMP.length - 1)),
  );
  return {
    backgroundColor: RAMP[idx],
    // Ink keeps at least 4.5:1 up through index 9 (#b87a17); white only
    // clears it from #8e5a10 onward.
    color: idx >= 10 ? "#ffffff" : "#18181b",
  };
}

function formatPct(pct: number): string {
  if (pct === 0) return "0";
  if (pct < 1) return "<1";
  return String(Math.round(pct));
}

// Debounce the free-text client filter so we issue one query after typing
// settles rather than one per keystroke.
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function RetentionTab() {
  const [source, setSource] = useState<RetentionSource>("sessions");
  const [weeks, setWeeks] = useState(12);
  const [clientId, setClientId] = useState("");
  const debouncedClientId = useDebounced(clientId, 400);

  const clientIds = debouncedClientId
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const { data, loading, error } = useRetention(source, weeks, clientIds);
  const meta = SOURCE_META[source];

  const cohorts = data?.cohorts ?? [];
  const maxOffset = Math.max(0, ...cohorts.map((c) => c.active.length - 1));
  const hasData = cohorts.some((c) => c.total > 0);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 pt-4">
          <Select
            value={source}
            onValueChange={(v) => setSource(v as RetentionSource)}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(SOURCE_META) as RetentionSource[]).map((s) => (
                <SelectItem key={s} value={s}>
                  {SOURCE_META[s].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={String(weeks)}
            onValueChange={(v) => setWeeks(Number(v))}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WEEK_OPTIONS.map((w) => (
                <SelectItem key={w} value={String(w)}>
                  Last {w} weeks
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {source === "refresh-tokens" && (
            <Input
              className="w-56"
              placeholder="Client IDs (comma-separated)"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
          )}
          <p className="text-xs text-muted-foreground">{meta.description}</p>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{meta.title}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-72">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : !hasData ? (
            <p className="text-sm text-muted-foreground">No data.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="border-separate border-spacing-0.5 text-xs tabular-nums">
                <thead>
                  <tr className="text-muted-foreground">
                    <th className="pr-2 text-right font-medium">Cohort week</th>
                    <th className="pr-2 text-right font-medium">{meta.unit}</th>
                    {Array.from({ length: maxOffset + 1 }, (_, k) => (
                      <th key={k} className="px-1 text-center font-medium">
                        +{k}w
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cohorts.map((c) => (
                    <tr key={c.cohort}>
                      <td className="pr-2 text-right text-muted-foreground">
                        {c.cohort}
                      </td>
                      <td className="pr-2 text-right text-muted-foreground">
                        {c.total.toLocaleString()}
                      </td>
                      {Array.from({ length: maxOffset + 1 }, (_, k) => {
                        if (k >= c.active.length) {
                          return <td key={k} />;
                        }
                        if (c.total === 0) {
                          return (
                            <td
                              key={k}
                              className="rounded-sm bg-muted text-center text-muted-foreground"
                            >
                              –
                            </td>
                          );
                        }
                        const pct = (c.active[k] / c.total) * 100;
                        return (
                          <td
                            key={k}
                            className="min-w-10 rounded-sm px-1 py-1.5 text-center"
                            style={cellStyle(pct)}
                            title={`${c.cohort} cohort, +${k} weeks: ${c.active[k].toLocaleString()} of ${c.total.toLocaleString()} ${meta.unit.toLowerCase()} still active (${pct.toFixed(1)}%)`}
                          >
                            {formatPct(pct)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <span>0%</span>
                <div
                  className="h-2.5 w-40 rounded-full border"
                  style={{
                    background: `linear-gradient(90deg, ${RAMP.join(",")})`,
                  }}
                />
                <span>100%</span>
                <span className="ml-1">
                  (square-root scale — early drop-off stays visible)
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
